import { PaymentStatus } from '@cpce/database/enums';
import { PAYMENT_STATUS_LABELS, type PaymentListItem } from '@cpce/shared';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { X } from 'lucide-react';
import { Badge, BadgeColor } from '@/components/Badge';
import { Button } from '@/components/Button';
import type { BadgeOption } from '@/components/table/table.types';
import { amountColumn, counterpartyColumn, emissionDateColumn } from './shared-payment-columns';

const columnHelper = createColumnHelper<PaymentListItem>();

const STATUS_BADGE_COLOR: Record<PaymentStatus, BadgeColor> = {
  [PaymentStatus.expected]: BadgeColor.ORANGE,
  [PaymentStatus.notDeposited]: BadgeColor.GRAY,
  [PaymentStatus.deposited]: BadgeColor.GREEN,
  [PaymentStatus.rejected]: BadgeColor.RED,
  [PaymentStatus.cancelled]: BadgeColor.RED,
};

export const PAYMENT_STATUS_OPTIONS: BadgeOption<PaymentStatus>[] = Object.values(
  PaymentStatus
).map((status) => ({
  value: status,
  label: PAYMENT_STATUS_LABELS[status],
  color: STATUS_BADGE_COLOR[status],
}));

interface BankDepositPaymentsColumnsOptions {
  // A "N° de chèque" column is meaningless for a method that carries no instrument number, so the
  // whole column is dropped rather than shown empty.
  hasInstrumentNumber: boolean;
  canReject: boolean;
  onReject: (payment: PaymentListItem) => void;
}

export function getBankDepositPaymentsColumns(
  options: BankDepositPaymentsColumnsOptions
): ColumnDef<PaymentListItem, never>[] {
  const { hasInstrumentNumber, canReject, onReject } = options;

  return [
    emissionDateColumn<PaymentListItem>(),
    counterpartyColumn<PaymentListItem>('Tiers'),
    ...(hasInstrumentNumber
      ? [
          columnHelper.accessor((row) => row.instrumentNumber ?? '-', {
            id: 'instrumentNumber',
            header: 'N° de chèque',
            meta: { filterType: 'text' },
          }),
        ]
      : []),
    amountColumn<PaymentListItem>(),
    columnHelper.accessor((row) => row.status, {
      id: 'status',
      header: 'Statut',
      cell: (info) => (
        <Badge color={STATUS_BADGE_COLOR[info.getValue()]}>
          {PAYMENT_STATUS_LABELS[info.getValue()]}
        </Badge>
      ),
      meta: { filterType: 'badge-enum', options: PAYMENT_STATUS_OPTIONS },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        // Only a deposited payment can be rejected: one already rejected is settled, and a
        // pending one has not reached the bank yet.
        if (!canReject || row.original.status !== PaymentStatus.deposited) return null;

        return (
          <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <Button variant="outline" color="red" icon={X} onClick={() => onReject(row.original)}>
              Rejet règlement
            </Button>
          </div>
        );
      },
      size: 180,
    }),
  ] as ColumnDef<PaymentListItem, never>[];
}
